import { create } from 'zustand'
import { getModeFromArray } from './utils'
import { useGraphStore } from '../stores/useGraphStore'
import { getGroupColor } from '../utils/graphColor'

import {
    fetchSentiment,
    fetchAddStatementAndGraph,
    deleteStatementsAndRecalculate,
    editStatementAndRecalculate,
    editCommonCategories,
} from './useApi'

const getSentimentPolarityFromScore = normalizedScore => {
    const sentimentTags = ['Positive', 'Negative', 'Neutral']
    let polarity
    if (normalizedScore > 0) {
        polarity = sentimentTags[0] // 'positive'
    } else if (normalizedScore < 0) {
        polarity = sentimentTags[1] // 'negative'
    } else {
        polarity = sentimentTags[2] // 'neutral'
    }
    return polarity
}

// Build statement objects from a backend `newEntries` payload.
// Shared by addNewStatement and editStatement.
const buildStatementsFromEntries = (
    newEntries,
    { contextName, fallbackText, fallbackTags = [] }
) =>
    (newEntries?.ids ?? []).map((id, i) => ({
        uid: parseInt(id),
        text: newEntries?.texts?.[i] ?? fallbackText,
        context: contextName,
        tags: newEntries?.tags?.[i] ?? fallbackTags,
        // strip `#` so pinnedNodes (plain words) match statement hashtags
        hashtags: (newEntries?.statementHashtags?.[String(id)] ?? []).map(h =>
            h.startsWith('#') ? h.slice(1) : h
        ),
        timestamp: newEntries?.timestamps?.[i] ?? Date.now(),
        visible: true,
        topCommunity: null,
        topOfCommunity: 'none',
    }))

// Statements added or edited client-side have no AI sentiment — drop the
// google cache so the next sentiment filter re-loads them.
const invalidateGoogleSentiment = (get, set, contextName) => {
    if (get().googleSentimentLoadedFor === contextName) {
        set({ googleSentimentLoadedFor: null, googleSentimentStatus: 'idle' })
    }
}

/**
 * Convert raw topics into the { id, name, color, concepts } format
 * expected by the AI ChatWidget (allClusters / insightClusters).
 * Normalizes node names (nodeName → name) for ChatWidget compatibility.
 * Color comes from the graph's own scale (utils/graphColor), keyed by the
 * community id — matching the rendered graph.
 */
const topicsToClusters = topics => {
    return (topics || []).map(topic => {
        const concepts = (topic.nodes || []).map(node => ({
            ...node,
            name: node.name || node.nodeName || node.label || '',
        }))

        return {
            id: topic.id,
            name:
                topic.aiName ||
                concepts
                    .slice(0, 3)
                    .map(c => c.name)
                    .join(', '),
            color: getGroupColor(topic.id),
            concepts,
        }
    })
}

/**
 * Derive insightClusters (the triad of 1–3 communities the AI bridges by default).
 *
 * Reader, not decider — the structure-aware choice is made once at graph build
 * time by infrasonic (`attributes.insight_clusters`, sized 1–3 by cluster count;
 * see docs/graph/insight-clusters-selection.md). Resolution order:
 *   1. attributes.insight_clusters[0].clusters → map community ids against allClusters (1–3)
 *   2. fallback (attribute absent — older cached graphs): gaps[0]'s pair,
 *      VERBATIM payload order (no re-sorting; the graph displays the same gaps[0])
 *   3. fallback (no gaps): first + last cluster (most + least influential)
 */
const deriveInsightClusters = allClusters => {
    const attributes = useGraphStore.getState().graphData
        ?.entriesAndGraphOfContext?.graph?.graphologyGraph?.attributes

    if (allClusters.length < 2) {
        return allClusters.length === 1 ? [allClusters[0]] : []
    }

    const findCluster = communityId =>
        allClusters.find(c => c.id === parseInt(communityId))

    // 1. Prefer the backend's build-time insight_clusters decision. The attribute
    //    is a depth-indexed array; entry 0 is the default the frontend highlights:
    //    { gapDepth, rule, clusters: [{ community, role }] }. Take the community
    //    ids, map to loaded clusters, keep matches (preserving order).
    const insightEntries = attributes?.insight_clusters
    if (Array.isArray(insightEntries) && insightEntries.length > 0) {
        const insightClusterObjects = insightEntries[0]?.clusters
        if (Array.isArray(insightClusterObjects)) {
            const matched = insightClusterObjects
                .map(c => findCluster(c.community))
                .filter(Boolean)
            if (matched.length >= 1) {
                return matched
            }
        }
    }

    // 2. Fallback to the top gap (gaps[0], verbatim) when insight_clusters is
    //    absent or its communities aren't in the loaded clusters.
    const gaps = attributes?.gaps
    if (Array.isArray(gaps) && gaps.length > 0) {
        const topGap = gaps[0]
        const fromCluster = findCluster(topGap.from.community)
        const toCluster = findCluster(topGap.to.community)
        if (fromCluster && toCluster) {
            return [fromCluster, toCluster]
        }
    }

    // 3. Fallback: first and last (most and least influential)
    return [allClusters[0], allClusters[allClusters.length - 1]]
}

export const useZustand = create((set, get) => ({
    currentContext: null,
    // Setter for currentContext — called by the cross-store reconciler in
    // useContextsStore.{renameContext,removeContext} so the active context
    // tracks rename/remove of the matching row.
    setCurrentContext: name => set({ currentContext: name }),
    // Resolved type of the context currently being viewed (e.g. 'STANDARD',
    // 'SEO', 'NICHE', 'META', 'KWRDS', ...). Denormalized from the contexts
    // tuple (index 3) + label-prefix inference at bootstrap so downstream
    // consumers don't have to re-resolve it. Null until a context loads.
    currentContextType: null,
    setCurrentContextType: type => set({ currentContextType: type }),
    setSeededEmptyContext: value => set({ seededEmptyContext: !!value }),
    // One-shot intent: the empty-graph "Add statements" link asks the
    // statements panel to open with the add-statement form revealed.
    // Consumed and cleared by StatementEditor (same pattern as
    // forceRequestMode).
    addStatementRequested: false,
    requestAddStatement: () => set({ addStatementRequested: true }),
    clearAddStatementRequest: () => set({ addStatementRequested: false }),
    // One-shot intent: start voice input as soon as the add-statement form
    // opens (set by the import dialog's mic menu alongside
    // requestAddStatement). Holds `{ source, includeMic }` — see
    // utils/voiceSource.js. Consumed and cleared by StatementTextarea.
    voiceInputRequested: false,
    requestVoiceInput: (options = { source: 'mic', includeMic: true }) =>
        set({ voiceInputRequested: options }),
    clearVoiceInputRequest: () => set({ voiceInputRequested: false }),
    // Backend compare mode the current graph was loaded with, derived from URL
    // params at bootstrap: null (no comparison) | 'intersection' | 'difference'
    // | 'merge'. Denormalized so the graph (via LazyGraph) can read how the
    // displayed graph was assembled without re-parsing the URL.
    currentCompareMode: null,
    setCurrentCompareMode: mode => set({ currentCompareMode: mode }),
    additionalContexts: [],
    tags: new Set(),
    loadedContextList: false,
    loadedStatements: false,
    loadedTopics: false,
    doNotSaveGraph: false,
    // True when the current context was opened before it existed on the
    // server and we seeded an empty editable graph (see bootstrapContextView).
    // The first successful statement-add creates the context server-side and
    // promotes it in place (promoteSeededContext), then clears this flag.
    seededEmptyContext: false,
    topics: [],
    statements: {},
    sentimentLoading: false,
    sentimentError: null,
    sentimentCache: {},
    googleSentimentStatus: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
    googleSentimentLoadedFor: null, // context name google sentiment is loaded for
    // Legacy analytics-panel mode only; loadGoogleSentiments doesn't update it.
    currentSentimentMode: 'wink',
    currentGraphMode: 'concepts', // 'concepts' | 'topics' | 'gaps'
    setCurrentGraphMode: mode => set({ currentGraphMode: mode }),
    // Initial state of the graph's controls / analytics panel — passed to
    // `@infranodus/infranodus-graph`'s `controls` prop. Values:
    //   'contracted' → start as compact pill,
    //   'expanded'   → start fully expanded,
    //   null         → let the package use its own heuristic
    //                  (iframe / narrow viewport → contracted, else expanded).
    // Seeded by bootstrap from `?controls=` so share/embed URLs can pin the
    // panel state. Emitted back into share URLs by `generateShareURL`.
    currentGraphControlsMode: null,
    setCurrentGraphControlsMode: mode => set({ currentGraphControlsMode: mode }),
    // True once the `<ChatWidget>` from `@infranodus/infranodus-ai` has
    // finished its hydration (chat-list + context-settings fetch) and is
    // ready to consume `forceRequestMode`. The auto-message gate in
    // `home/index.jsx` waits on this — firing `setForceRequestMode` before
    // the widget is ready silently no-ops (the widget's effect calls its
    // send action against an uninitialised chat session).
    // Flipped by `AIChat/index.jsx` from the widget's `onReady` callback.
    isAiChatReady: false,
    setIsAiChatReady: ready => set({ isAiChatReady: !!ready }),
    // Whether the host will auto-send a seed message for the current context
    // (single-statement cold-start in the AI-chat regime). Passed to
    // `<ChatWidget willAutoSend>` so its AI-insights view gates deterministically
    // instead of racing a settle timer. true = seed coming, false = no seed,
    // null = not yet known (statements still loading).
    willAutoSend: null,
    setWillAutoSend: value => set({ willAutoSend: value }),
    recalculateAnalytics: false,
    panelOpen: true,
    comparisonMode: null,
    statementsViewType: 'overview',
    insightClusters: [],
    allClusters: [],
    forceRequestMode: {
        name: 'none',
        useInsightClusters: false,
        customInstruction: '',
    },
    autoAddFromAiChat: false,
    addUserMessagesToGraph: false,
    useNewAiModule: false,
    setAutoAddFromAiChat: value => set({ autoAddFromAiChat: value }),
    setAddUserMessagesToGraph: value => set({ addUserMessagesToGraph: value }),
    setUseNewAiModule: value => set({ useNewAiModule: value }),
    updateRecalculateAnalytics: value => set({ recalculateAnalytics: value }),
    setForceRequestMode: mode => {
        console.log('[InfraNodus] 🎯 Setting forceRequestMode:', mode)
        set({ forceRequestMode: mode })
    },
    resetForceRequestMode: () => {
        console.log('[InfraNodus] 🔄 Resetting forceRequestMode to default')
        set({
            forceRequestMode: {
                name: 'none',
                useInsightClusters: false,
                customInstruction: '',
            },
        })
    },
    // NOTE: updateInsightClusters/updateAllClusters (graphFactory readers,
    // triggered by legacy GraphUpdate/AnalyticsUpdate events) and the
    // standalone setInsightClusters/setAllClusters setters were removed:
    // they overwrote the gap-derived clusters computed by setTopics below.
    // setTopics is the ONLY writer of allClusters/insightClusters.
    // See docs/legacy-sigma-removal-plan.md (Stage 1.1).
    // getContextList / getPublicContextList from master were also dropped:
    // contexts state moved to useContextsStore (see selectOwnerContexts /
    // selectOwnedContexts), so reading from get().contexts here would
    // always return undefined.
    setTopics: (topics, loadedTopics) => {
        const allClusters = topicsToClusters(topics)
        const insightClusters = deriveInsightClusters(allClusters)
        set({ topics, loadedTopics, allClusters, insightClusters })
    },
    setAdditionalContexts: additionalContexts =>
        set({ additionalContexts: additionalContexts || [] }),

    // Returns cached statements - data is loaded by bootstrap store
    // This function now primarily returns cached data
    getStatementsList: (
        username,
        contextName,
        addContextNames,
        isPublic,
        useNewGraphAPI = false
    ) => {
        console.log(
            '[useZustand] Getting cached statements for context:',
            contextName
        )

        // Return cached statements if already loaded
        if (get().loadedStatements && get().statements[contextName]) {
            return get().statements[contextName]
        }

        // If not loaded yet but we have statements in the store, return them
        const currentStatements = get().statements[contextName]
        if (currentStatements && currentStatements.length > 0) {
            return currentStatements
        }

        console.warn(
            '[useZustand] No cached statements found for context:',
            contextName
        )
        return []
    },
    updateStatementsWithTopics: async () => {
        const allStatements = get().statements

        const communitiesToStatements = {}

        Object.keys(allStatements).forEach(contextName => {
            const statementsList = allStatements[contextName]

            const statementsWithMetaData = statementsList.map(statement => {
                // Get hashtags for the statement - should come from API now
                const hashtags = statement.hashtags || []

                // Calculate top community for the statement using new graph store
                const statementCommunities = []
                if (hashtags && hashtags.length > 0) {
                    hashtags.forEach(hashtag => {
                        const word = hashtag.startsWith('#')
                            ? hashtag.slice(1)
                            : hashtag
                        // Use compatibility layer (works with both new store and old graphFactory)
                        const community = useGraphStore
                            .getState()
                            .getCommunityOfNode(word)
                        if (community !== undefined) {
                            if (!communitiesToStatements[community]) {
                                communitiesToStatements[community] = []
                            }
                            communitiesToStatements[community].push(
                                statement.uid
                            )

                            statementCommunities.push(community)
                        }
                    })
                }

                // Get the most frequent community using getModeFromArray
                const topStatementCommunity =
                    statementCommunities.length > 0
                        ? getModeFromArray(statementCommunities)
                        : null

                // The most frequent community for the statement doesn't mean this statement is the top one for this community
                // So after we get data from the graph we add topOfCommunity that signifies whether this statement is the top one for any of the communities

                return {
                    ...statement,
                    hashtags: hashtags,
                    topCommunity: topStatementCommunity,
                }
            })

            allStatements[contextName] = statementsWithMetaData
        })

        const communitiesToTopStatements = {}

        Object.keys(communitiesToStatements).forEach(community => {
            const statements = communitiesToStatements[community]

            const topStatementOfCommunity = getModeFromArray(statements)
            communitiesToTopStatements[community] = topStatementOfCommunity
        })

        Object.keys(allStatements).forEach(contextName => {
            const statementsWithTopOfCommunityData = allStatements[
                contextName
            ].map(statement => {
                const topOfCommunity = Object.keys(
                    communitiesToTopStatements
                ).find(
                    community =>
                        communitiesToTopStatements[community] === statement.uid
                )
                if (topOfCommunity != undefined) {
                    return {
                        ...statement,
                        topOfCommunity: parseInt(topOfCommunity),
                    }
                } else {
                    return {
                        ...statement,
                        topOfCommunity: 'none', // not a valid community
                    }
                }
            })
            allStatements[contextName] = statementsWithTopOfCommunityData
        })

        set({ statements: allStatements })
    },
    updateStatementsHighlights: async statementsWithHighlights => {
        const allStatements = { ...get().statements }

        const grayedOutMap = {}
        const visibilityMap = {}

        statementsWithHighlights.forEach(item => {
            if (item.uid !== undefined) {
                visibilityMap[item.uid] = true
            }
            if (item.uid !== undefined && item.grayedOut !== undefined) {
                grayedOutMap[item.uid] = item.grayedOut
            }
        })

        // Update all arrays in the object
        Object.keys(allStatements).forEach(contextName => {
            const statementsInContext = allStatements[contextName]

            // Update each element if it has a matching uid in the map
            for (let i = 0; i < statementsInContext.length; i++) {
                const item = statementsInContext[i]
                if (
                    item.uid !== undefined &&
                    grayedOutMap[item.uid] !== undefined
                ) {
                    statementsInContext[i] = {
                        ...item,
                        grayedOut: grayedOutMap[item.uid],
                    }
                }
                if (item.uid !== undefined) {
                    statementsInContext[i] = {
                        ...statementsInContext[i],
                        visible: visibilityMap[item.uid] || false,
                    }
                }
            }
        })

        const finalStatements = { ...allStatements }

        set({ statements: finalStatements })
    },
    removeStatements: async statementIds => {
        const context = get().currentContext
        const {
            removedIds,
            entriesAndGraphOfContext,
        } = await deleteStatementsAndRecalculate(statementIds, context)
        get().removeStatementsFromStore(statementIds)
        return { removedIds, entriesAndGraphOfContext }
    },
    editStatement: async (text, tags, statementId) => {
        const contextName = get().currentContext
        const {
            newEntries,
            entriesAndGraphOfContext,
        } = await editStatementAndRecalculate(
            statementId,
            text,
            tags,
            contextName,
            false
        )

        const editedStatements = buildStatementsFromEntries(newEntries, {
            contextName,
            fallbackText: text,
            fallbackTags: tags,
        })
        const uid = editedStatements[0]?.uid ?? null

        if (editedStatements.length === 1 && uid === Number(statementId)) {
            get().replaceStatementInStore(editedStatements[0])
        } else {
            // The text was split into several statements: the server replaced
            // the old one with new ones, so drop the old uid and append them.
            const allStatements = get().statements
            const withoutOld = {}
            Object.keys(allStatements).forEach(key => {
                withoutOld[key] = allStatements[key].filter(
                    s => s.uid !== statementId
                )
            })
            set({ statements: withoutOld })
            await get().addStatementsToStore(editedStatements)
        }

        return { uid, entriesAndGraphOfContext }
    },

    // Keeps the statement's slot, timestamp and ordering fields and takes the
    // text-derived ones from the server. Client-side sentiment belongs to the
    // old text, so it is dropped along with the google cache.
    replaceStatementInStore: edited => {
        const currentContext = get().currentContext
        const allStatements = get().statements
        const currentStatements = allStatements[currentContext] || []

        set({
            statements: {
                ...allStatements,
                [currentContext]: currentStatements.map(statement =>
                    statement.uid === edited.uid
                        ? {
                              ...statement,
                              text: edited.text,
                              tags: edited.tags,
                              hashtags: edited.hashtags,
                              sentiment: undefined,
                          }
                        : statement
                ),
            },
        })
        edited.tags.forEach(tag => get().tags.add(tag))
        invalidateGoogleSentiment(get, set, currentContext)
    },

    editCommonCategories: async (
        statementIds,
        tagsToAdd = [],
        tagsToRemove = []
    ) => {
        // Use the correct API for adding/removing tags
        const editCommonCategoriesResponse = await editCommonCategories(
            statementIds,
            tagsToAdd,
            tagsToRemove
        )

        // Update local state
        const newStatements = {}
        Object.keys(get().statements).forEach(key => {
            const statements = get().statements[key]
            const updatedStatements = statements.map(statement => {
                if (statementIds.includes(statement.uid)) {
                    let currentTags = Array.isArray(statement.tags)
                        ? [...statement.tags]
                        : []
                    // Remove tags
                    currentTags = currentTags.filter(
                        tag => !tagsToRemove.includes(tag)
                    )
                    // Add new tags (no duplicates)
                    tagsToAdd.forEach(tag => {
                        if (!currentTags.includes(tag)) currentTags.push(tag)
                    })
                    return {
                        ...statement,
                        tags: currentTags,
                    }
                }
                return statement
            })
            newStatements[key] = [...updatedStatements]
        })
        // Update the tags set in store
        const newlyAddedTags = tagsToAdd.filter(tag => !get().tags.has(tag))
        const newTags =
            newlyAddedTags.length > 0
                ? { tags: new Set([...get().tags, ...newlyAddedTags]) }
                : {}
        set({ statements: newStatements, ...newTags })

        return editCommonCategoriesResponse
    },
    addNewTag: tag => {
        const newTags = new Set([...get().tags, tag])
        set({ tags: newTags })
    },
    removeStatementsFromStore: statementIds => {
        const newStatements = {}
        const statementIdsInt = statementIds.map(id => parseInt(id))

        Object.keys(get().statements).forEach(key => {
            newStatements[key] = get().statements[key].filter(
                statement => !statementIdsInt.includes(statement.uid)
            )
        })

        const newTags = get().extractAvaialbleTags(newStatements)
        set({ statements: newStatements, tags: newTags })

        return newStatements
    },

    extractAvaialbleTags: statements => {
        const newTags = new Set(
            Object.values(statements)
                .flat()
                .map(statement => statement.tags)
                .flat()
        )
        return newTags
    },
    updatePanelOpen: async panelOpen => {
        const updatePanelLocalStorage = panelOpen => {
            // jQuery is an EJS-page global absent on standalone React pages
            // like /import/* — the legacy #statements-link only exists there
            const jq = window.$
            if (panelOpen == false) {
                localStorage.setItem('folded', 1)
                if (!jq) return
                if (
                    window.userFactory &&
                    userFactory.getBackground() == 'dark'
                ) {
                    jq('#statements-link').addClass('statements-link-on-dark')
                } else {
                    jq('#statements-link').addClass('statements-link-on')
                }
            } else {
                localStorage.setItem('folded', 0)

                if (!jq) return
                jq('#statements-link').removeClass('statements-link-on-dark')
                jq('#statements-link').removeClass('statements-link-on')
            }
        }
        updatePanelLocalStorage(panelOpen)
        set({ panelOpen })
    },
    updateComparisonMode: comparisonMode => {
        set({ comparisonMode })
    },
    updateStatementsViewType: async statementsViewType => {
        if (window.myEventEmitter) {
            myEventEmitter.on('GraphLoaded', async () => {
                if (statementsViewType == 'missing') {
                    const currentContext = [userFactory.getCurrentContext()]
                    const additionalContexts =
                        userFactory.getAddContext() &&
                        userFactory.getAddContext().length > 0
                            ? userFactory.getAddContext()
                            : []
                    filterPanel.applyCategoryFilter(
                        additionalContexts,
                        currentContext,
                        'filter_statements'
                    )
                }
            })
        }

        set({ statementsViewType })
    },
    analyzeSentiments: async (
        user,
        modelToUse = 'wink',
        statements = [],
        isPublic = false
    ) => {
        if (get().sentimentCache[modelToUse] && statements.length === 0) {
            return get().sentimentCache[modelToUse]
        }
        const currentContext = get().currentContext
        const loadedStatements = get().statements[currentContext]
        if (!loadedStatements && statements.length === 0) {
            console.log('!!! No statements found !!!')
            return {}
        }
        // extract into fetchSentiments
        const statementsToAnalyse =
            statements.length > 0
                ? statements
                : get().statements[currentContext]
        const sentiments = await fetchSentiment(
            statementsToAnalyse,
            modelToUse,
            user,
            currentContext,
            isPublic
        )

        set(state => ({
            sentimentCache: {
                ...state.sentimentCache,
                [modelToUse]: {
                    ...state.sentimentCache[modelToUse],
                    ...sentiments,
                },
            },
        }))
        return sentiments
    },
    // Lazily load google sentiment for the current context only, guarded so a
    // missing/partial score never throws.
    loadGoogleSentiments: async ({ user, isPublic = false }) => {
        const ctx = get().currentContext
        // Session cache: skip refetch if already loaded for this context.
        if (get().googleSentimentLoadedFor === ctx) {
            return get().statements[ctx] || []
        }

        set({ googleSentimentStatus: 'loading' })
        try {
            const statements = get().statements[ctx] || []
            // Explicit statements → analyzeSentiments fetches this exact set, not
            // a stale cross-context cache.
            const scores = await get().analyzeSentiments(
                user,
                'google',
                statements,
                isPublic
            )

            // Google returns 200 with null scores on failure; if none scored, fail
            // instead of applying a no-op filter under a "ready" toast.
            const scoredCount = statements.filter(
                s => scores[s.uid]?.normalizedScore != null
            ).length
            if (statements.length > 0 && scoredCount === 0) {
                throw new Error('google returned no usable sentiment scores')
            }

            const reassessed = statements.map(statement => {
                const score = scores[statement.uid]?.normalizedScore
                if (score === undefined || score === null) return statement
                return {
                    ...statement,
                    sentiment: getSentimentPolarityFromScore(score),
                }
            })

            set(state => ({
                statements: { ...state.statements, [ctx]: reassessed },
                googleSentimentLoadedFor: ctx,
                googleSentimentStatus: 'ready',
            }))
            // Caller syncs the filter store. We don't set currentSentimentMode —
            // it would flip the legacy analytics panel (recalculateFilterEffects).
            return reassessed
        } catch (err) {
            console.error('[useZustand] loadGoogleSentiments failed:', err)
            set({ googleSentimentStatus: 'error' })
            throw err
        }
    },
    addNewStatement: async (text, tags, extractEntities) => {
        const contextName = get().currentContext

        const {
            newEntries,
            entriesAndGraphOfContext,
        } = await fetchAddStatementAndGraph(
            text,
            tags,
            extractEntities,
            contextName
        )

        const newStatements = buildStatementsFromEntries(newEntries, {
            contextName,
            fallbackText: text,
        })

        await get().addStatementsToStore(newStatements)

        const uid = newStatements[0]?.uid ?? null
        return { uid, entriesAndGraphOfContext }
    },
    addStatementsToStore: async newStatements => {
        const currentContext = get().currentContext
        const allStatements = get().statements
        const currentStatements = allStatements[currentContext] || []

        if (newStatements.length === 0) return

        const updatedStatements = {
            ...allStatements,
            [currentContext]: [...currentStatements, ...newStatements],
        }
        // Updates tags in store
        newStatements.forEach(statement =>
            statement.tags.forEach(tag => get().tags.add(tag))
        )
        set({ statements: updatedStatements })
        invalidateGoogleSentiment(get, set, currentContext)
    },

    addPrivateStatementsToStore: async (
        privateStatements,
        contextName,
        entryuids,
        statementHashtags = {}
    ) => {
        const allStatements = get().statements
        const currentStatements = allStatements[contextName] || []

        if (!privateStatements || privateStatements.length === 0) return

        // Convert private statements to the expected format
        const formattedStatements = privateStatements.map((text, index) => {
            const uid =
                entryuids && entryuids[index]
                    ? parseInt(entryuids[index])
                    : Date.now() + index

            // Get hashtags for this statement from the provided mapping
            const hashtags = statementHashtags[uid.toString()] || []

            return {
                uid: uid,
                text: text,
                tags: [], // Private statements start with no tags
                hashtags: hashtags, // Use hashtags from backend
                context: contextName,
                timestamp: new Date().toISOString(),
                visible: true,
            }
        })

        // Enrich statements with communities
        const communitiesToStatements = {}

        const statementsWithMetadata = formattedStatements.map(statement => {
            // Calculate top community for the statement using the hashtags we have
            const statementCommunities = []
            if (statement.hashtags && statement.hashtags.length > 0) {
                statement.hashtags.forEach(hashtag => {
                    const word = hashtag.startsWith('#')
                        ? hashtag.slice(1)
                        : hashtag
                    // Use compatibility layer (works with both new store and old graphFactory)
                    const community = useGraphStore
                        .getState()
                        .getCommunityOfNode(word)
                    if (community !== undefined) {
                        if (!communitiesToStatements[community]) {
                            communitiesToStatements[community] = []
                        }
                        communitiesToStatements[community].push(statement.uid)
                        statementCommunities.push(community)
                    }
                })
            }

            // Get the most frequent community
            const topStatementCommunity =
                statementCommunities.length > 0
                    ? getModeFromArray(statementCommunities)
                    : null

            return {
                ...statement,
                topCommunity: topStatementCommunity,
                visible: true,
            }
        })

        // Calculate top statements for communities

        const communitiesToTopStatements = {}

        Object.keys(communitiesToStatements).forEach(community => {
            const statements = communitiesToStatements[community]
            const topStatementOfCommunity = getModeFromArray(statements)
            communitiesToTopStatements[community] = topStatementOfCommunity
        })

        // Add top of community data
        const statementsWithTopOfCommunityData = statementsWithMetadata.map(
            statement => {
                const topOfCommunity = Object.keys(
                    communitiesToTopStatements
                ).find(
                    community =>
                        communitiesToTopStatements[community] === statement.uid
                )
                if (topOfCommunity != undefined) {
                    return {
                        ...statement,
                        topOfCommunity: parseInt(topOfCommunity),
                    }
                } else {
                    return {
                        ...statement,
                        topOfCommunity: 'none', // not a valid community
                    }
                }
            }
        )

        // Update the store with enriched statements
        const updatedStatements = {
            ...allStatements,
            [contextName]: [
                ...currentStatements,
                ...statementsWithTopOfCommunityData,
            ],
        }

        // Update tags in store
        statementsWithTopOfCommunityData.forEach(statement => {
            if (statement.tags && statement.tags.length > 0) {
                statement.tags.forEach(tag => get().tags.add(tag))
            }
        })

        set({
            statements: updatedStatements,
            currentContext: contextName,
            loadedStatements: true,
            doNotSaveGraph: true,
        })

        return statementsWithTopOfCommunityData
    },

    reassessSentiments: (sentiments, sentimentMode) => {
        const allStatements = get().statements

        // Update all arrays in the object
        Object.keys(allStatements).forEach(currentContext => {
            const currentStatements = allStatements[currentContext] || []
            const reassedStatements = currentStatements.map(statement => {
                const score = sentiments[statement.uid]?.normalizedScore
                if (score == null) return statement
                return {
                    ...statement,
                    sentiment: getSentimentPolarityFromScore(score),
                }
            })

            allStatements[currentContext] = reassedStatements
        })

        const updatedStatements = { ...allStatements }

        set({
            statements: updatedStatements,
            loadedStatements: true,
            currentSentimentMode: sentimentMode,
        })
        return updatedStatements
    },
}))
